// Each moment of a match that has a sound: its family and its layers. The
// numbers are from the proposal page, `design/sounds/wood-sounds.html`.
// Garrett picked the family of each moment on 2026-10-11. Pure.

import { SOUND_LEVEL } from '../constants';
import { air, type Layer, renderLayers, sweep } from './parts';
import { type SoundFamily, VOICES, type Voice } from './voices';

/** What the layers of a moment are played at. */
type Take = { layers: Layer[]; level: number };

type Moment = {
  family: SoundFamily;
  /** How many forms the moment has. A caller asks for one by number. One when absent. */
  variants?: number;
  take: (voice: Voice, variant: number) => Take;
};

/** Moon is this ratio lower than Sun: a fourth. */
const FOURTH_DOWN = 0.749;
/** The pitch of each bounce of the die, in hertz. Each bounce is quieter by `BOUNCE_FADE`. */
const BOUNCE_HZ = [760, 900, 700, 840, 650] as const;
const BOUNCE_FADE = 0.1;
/** A hop of the wheel highlight is 1,500 Hz times one of these, in turn. */
const HOP_HZ = 1500;
const HOP_RATIOS = [1, 1.06, 0.95, 1.02] as const;
const HOP_LEVEL = 0.7;
/** A coin of energy that leaves the die ends on this pitch. The second coin of a roll is a major third higher. */
const ENERGY_OUT_HZ = 880;
const ENERGY_OUT_RATIOS = [1, 1.26] as const;
/**
 * A coin that drops into the first pip has this pitch. Each pip after it is
 * one step up a scale of five notes. The last step is for energy over the row.
 */
const ENERGY_IN_HZ = 330;
const ENERGY_IN_STEPS = [1, 1.125, 1.25, 1.5, 1.667, 2, 2.25] as const;

/** Taps one after the other: the pitch and the moment of each, with a gain and a length from its place. */
const taps = (
  voice: Voice,
  notes: readonly (readonly [hz: number, at: number])[],
  gainAt: (index: number) => number,
  longAt: (index: number) => number = () => 1,
): Layer[] => notes.flatMap(([hz, at], index) => voice.tap(hz, at, gainAt(index), longAt(index)));

const pick = <T>(values: readonly T[], index: number): T => values[index % values.length] as T;

const MOMENTS = {
  // ---- The die tumbles: block ----
  // One bounce off an edge of the board frame. Each has another pitch.
  dieBounce: {
    family: 'block',
    variants: BOUNCE_HZ.length,
    take: (v, variant) => ({
      layers: v.tap(pick(BOUNCE_HZ, variant)),
      level: SOUND_LEVEL.plain * (1 - variant * BOUNCE_FADE),
    }),
  },
  // The die stops with the face up: two close knocks, as a die rocks once, and a low body.
  dieLand: {
    family: 'block',
    take: (v) => ({
      layers: [...v.tap(620, 0, 1, 1.3), ...v.tap(520, 0.07, 0.6), ...v.tap(260, 0, 0.5, 2)],
      level: SOUND_LEVEL.loud,
    }),
  },

  // ---- The wheel highlight: block ----
  wheelHop: {
    family: 'block',
    variants: HOP_RATIOS.length,
    take: (v, variant) => ({
      layers: v.tap(HOP_HZ * pick(HOP_RATIOS, variant), 0, 1, 0.6),
      level: SOUND_LEVEL.quiet * HOP_LEVEL,
    }),
  },
  // The highlight stops on the rolled face.
  wheelStop: {
    family: 'block',
    take: (v) => ({ layers: v.tap(1100, 0, 1, 1.2), level: SOUND_LEVEL.quiet }),
  },

  // ---- The spell card: block ----
  // The card comes up on the stage: paper air that rises, then a knock as it stands.
  cardUp: {
    family: 'block',
    take: (v) => ({
      layers: [air(700, 3000, 0.14, 0, 0.7 * v.air), ...v.tap(880, 0.13, 0.9)],
      level: SOUND_LEVEL.plain,
    }),
  },
  cardDown: {
    family: 'block',
    take: (v) => ({
      layers: [air(2800, 700, 0.12, 0, 0.7 * v.air), ...v.tap(600, 0.11, 0.8)],
      level: SOUND_LEVEL.plain,
    }),
  },

  // ---- The color of the spell: log ----
  ally: {
    family: 'log',
    take: (v) => ({
      layers: [...v.tap(660, 0, 0.8), ...v.tap(880, 0.08, 0.9), ...v.tap(1320, 0.16, 1, 1.5)],
      level: SOUND_LEVEL.plain,
    }),
  },
  enemy: {
    family: 'log',
    take: (v) => ({
      layers: [...v.tap(880, 0, 1), ...v.tap(620, 0.09, 0.9), ...v.tap(415, 0.19, 1, 1.6)],
      level: SOUND_LEVEL.plain,
    }),
  },
  any: {
    family: 'log',
    take: (v) => ({
      layers: [
        ...v.tap(700, 0, 1),
        sweep(520, 190, 0.07, 0.9 * v.bend + 0.3, {
          glide: 0.06,
          halfLife: 0.08,
          seconds: 0.3,
          wobble: [22, 0.05],
        }),
        ...v.tap(260, 0.3, 0.8, 1.6),
      ],
      level: SOUND_LEVEL.plain,
    }),
  },
  board: {
    family: 'log',
    take: (v) => ({
      layers: taps(
        v,
        [
          [700, 0],
          [940, 0.03],
          [620, 0.07],
          [1050, 0.1],
          [780, 0.15],
          [560, 0.19],
          [880, 0.25],
        ],
        (index) => 0.75 - index * 0.05,
      ),
      level: SOUND_LEVEL.plain,
    }),
  },

  // ---- Lift and move: log ----
  lift: {
    family: 'log',
    take: (v) => ({
      layers: [...v.tap(980, 0, 0.8, 0.8), air(900, 2200, 0.05, 0, 0.25 * v.air)],
      level: SOUND_LEVEL.quiet,
    }),
  },
  // A piece lands on its square. The second form is Moon, a fourth lower.
  move: {
    family: 'log',
    variants: 2,
    take: (v, variant) => {
      const ratio = variant === 0 ? 1 : FOURTH_DOWN;
      return {
        layers: [...v.tap(540 * ratio, 0, 1, 1.2), ...v.tap(270 * ratio, 0, 0.45, 1.6)],
        level: SOUND_LEVEL.plain,
      };
    },
  },

  // ---- A piece leaves: block ----
  // A capture: the piece lands hard, the other piece is knocked over, and the cloud opens.
  capture: {
    family: 'block',
    take: (v) => ({
      layers: [
        ...v.tap(500, 0, 1, 1.3),
        ...v.tap(250, 0, 0.6, 2),
        ...v.tap(820, 0.06, 0.6),
        ...v.tap(690, 0.11, 0.4),
        air(1600, 500, 0.22, 0.03, 0.5 * v.air, 0.07),
      ],
      level: SOUND_LEVEL.loud,
    }),
  },
  // A piece leaves with no capture: a blast, a deadly square, a sacrifice. Only the cloud.
  cloud: {
    family: 'block',
    take: (v) => ({
      layers: [
        air(1800, 450, 0.26, 0, 0.8 * v.air + 0.2, 0.08),
        ...v.tap(380, 0, 0.5, 1.5),
        ...v.tap(300, 0.08, 0.3, 1.5),
      ],
      level: SOUND_LEVEL.plain,
    }),
  },

  // ---- A condition: twig ----
  // A badge pops onto a piece.
  badge: {
    family: 'twig',
    take: (v) => ({
      layers: [...v.tap(1240, 0, 0.8, 0.7), ...v.tap(1650, 0.045, 0.6, 0.7)],
      level: SOUND_LEVEL.quiet,
    }),
  },
  // A condition ends.
  timer: {
    family: 'twig',
    take: (v) => ({
      layers: [...v.tap(1100, 0, 0.7, 0.7), ...v.tap(830, 0.06, 0.6, 0.9)],
      level: SOUND_LEVEL.quiet,
    }),
  },

  // ---- Energy: log and twig ----
  // A coin of energy leaves the die: a short tone that bends up. The second coin of a roll is higher.
  energyOut: {
    family: 'twig',
    variants: ENERGY_OUT_RATIOS.length,
    take: (v, variant) => {
      const hz = ENERGY_OUT_HZ * pick(ENERGY_OUT_RATIOS, variant);
      return {
        layers: [
          sweep(hz * 0.55, hz, 0, v.bend, { glide: 0.028, halfLife: 0.03, seconds: 0.13 }),
          air(900, 2600, 0.06, 0, 0.2 * v.air),
        ],
        level: SOUND_LEVEL.quiet,
      };
    },
  },
  // A coin drops into its pip: a round tone that bends down to its note, on a low body.
  // The note goes up with the place of the pip, so a row that is more full sounds higher.
  // The second half of the forms is Moon, a fourth lower.
  energyIn: {
    family: 'log',
    variants: ENERGY_IN_STEPS.length * 2,
    take: (v, variant) => {
      const side = variant < ENERGY_IN_STEPS.length ? 1 : FOURTH_DOWN;
      const hz = ENERGY_IN_HZ * pick(ENERGY_IN_STEPS, variant) * side;
      return {
        layers: [
          sweep(hz * 1.9, hz, 0, 1, { glide: 0.022, halfLife: 0.05, seconds: 0.26 }),
          ...v.tap(hz, 0.012, 0.5, 1.2),
        ],
        level: SOUND_LEVEL.plain,
      };
    },
  },
  // Energy the side pays leaves its pip: a short tone that falls. The forms are those of `energyIn`.
  energySpend: {
    family: 'twig',
    variants: ENERGY_IN_STEPS.length * 2,
    take: (v, variant) => {
      const side = variant < ENERGY_IN_STEPS.length ? 1 : FOURTH_DOWN;
      const hz = ENERGY_IN_HZ * 2 * pick(ENERGY_IN_STEPS, variant) * side;
      return {
        layers: [sweep(hz, hz * 0.6, 0, v.bend, { glide: 0.03, halfLife: 0.025, seconds: 0.11 })],
        level: SOUND_LEVEL.quiet,
      };
    },
  },
  // The last coin of a cast lands in the slot of the skill: one low knock. The second form is Moon.
  energyPaid: {
    family: 'log',
    variants: 2,
    take: (v, variant) => {
      const ratio = variant === 0 ? 1 : FOURTH_DOWN;
      return {
        layers: [...v.tap(392 * ratio, 0, 1, 1.4), ...v.tap(196 * ratio, 0, 0.5, 1.8)],
        level: SOUND_LEVEL.plain,
      };
    },
  },
  // A skill the side can pay for now: two notes that go up.
  slotReady: {
    family: 'twig',
    take: (v) => ({
      layers: [...v.tap(980, 0, 0.7, 1.2), ...v.tap(1470, 0.08, 1, 1.8)],
      level: SOUND_LEVEL.quiet,
    }),
  },

  // ---- A tap that does nothing: twig ----
  refuse: {
    family: 'twig',
    take: (v) => ({
      layers: [...v.tap(330, 0, 1), ...v.tap(250, 0.1, 0.85, 1.3)],
      level: SOUND_LEVEL.plain,
    }),
  },

  // ---- Buttons and the menu: block ----
  press: {
    family: 'block',
    take: (v) => ({ layers: v.tap(1150, 0, 1, 0.8), level: SOUND_LEVEL.quiet }),
  },
  menuOpen: {
    family: 'block',
    take: (v) => ({
      layers: [
        air(900, 3000, 0.11, 0, 0.6 * v.air),
        ...v.tap(900, 0.1, 0.8),
        ...v.tap(1200, 0.14, 0.6),
      ],
      level: SOUND_LEVEL.quiet,
    }),
  },
  menuFold: {
    family: 'block',
    take: (v) => ({
      layers: [
        air(2800, 800, 0.1, 0, 0.6 * v.air),
        ...v.tap(1200, 0.09, 0.6),
        ...v.tap(700, 0.13, 0.8),
      ],
      level: SOUND_LEVEL.quiet,
    }),
  },

  // ---- History: twig ----
  back: {
    family: 'twig',
    take: (v) => ({
      layers: [...v.tap(900, 0, 0.8, 0.8), ...v.tap(700, 0.05, 0.7, 0.8)],
      level: SOUND_LEVEL.quiet,
    }),
  },
  forward: {
    family: 'twig',
    take: (v) => ({
      layers: [...v.tap(700, 0, 0.8, 0.8), ...v.tap(900, 0.05, 0.7, 0.8)],
      level: SOUND_LEVEL.quiet,
    }),
  },

  // ---- The turn of the player: block ----
  // Two knocks, as on a door.
  yourTurn: {
    family: 'block',
    take: (v) => ({
      layers: [...v.tap(740, 0, 0.9), ...v.tap(740, 0.12, 1)],
      level: SOUND_LEVEL.plain,
    }),
  },

  // ---- The end of a match: log ----
  win: {
    family: 'log',
    take: (v) => ({
      layers: taps(
        v,
        [
          [520, 0],
          [660, 0.11],
          [780, 0.22],
          [1040, 0.33],
          [1320, 0.5],
        ],
        (index) => 0.8 + index * 0.05,
        (index) => (index === 4 ? 2.5 : 1.2),
      ),
      level: SOUND_LEVEL.loud,
    }),
  },
  lose: {
    family: 'log',
    take: (v) => ({
      layers: [
        ...taps(
          v,
          [
            [780, 0],
            [620, 0.14],
            [465, 0.3],
            [310, 0.5],
          ],
          () => 0.9,
          (index) => (index === 3 ? 2.5 : 1.2),
        ),
        sweep(300, 150, 0.5, 0.5 * v.bend, { glide: 0.1, halfLife: 0.12, seconds: 0.5 }),
      ],
      level: SOUND_LEVEL.loud,
    }),
  },
} as const satisfies Record<string, Moment>;

export type SoundId = keyof typeof MOMENTS;

/**
 * The form of `energyIn` for a coin that drops into the pip at `place`, from
 * zero. Energy over the row has the last step.
 */
export const energyInForm = (place: number, isMoon: boolean): number =>
  Math.min(Math.max(place, 0), ENERGY_IN_STEPS.length - 1) + (isMoon ? ENERGY_IN_STEPS.length : 0);

/** The form of a moment for a number that counts up: the forms come in turn. */
export const soundVariant = (id: SoundId, count: number): number => {
  const moment: Moment = MOMENTS[id];
  return count % (moment.variants ?? 1);
};

/** The samples of one moment, at `SOUND_SAMPLE_RATE`. */
export const soundSamples = (id: SoundId, variant = 0): Float32Array => {
  const moment: Moment = MOMENTS[id];
  const { layers, level } = moment.take(VOICES[moment.family], variant);
  return renderLayers(layers, level);
};
